import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Heart, Trash2, MapPin, Bed, Bath, Square, AlertCircle,
    Search, Filter
} from 'lucide-react';
import { NotificationManager } from 'react-notifications';
import { useSavedProperties } from '../../../hooks/useDashboard';
import { formatCurrency } from '../../../constants/propertyConstants';

/**
 * SavedProperties Component
 * Displays user's saved/favorite properties
 */
const SavedProperties = () => {
    const navigate = useNavigate();
    const { properties, loading, error, unsaveProperty } = useSavedProperties();
    const [searchQuery, setSearchQuery] = useState('');
    const [removing, setRemoving] = useState(null);

    const handleRemove = async (propertyId) => {
        if (!window.confirm('Remove this property from your saved list?')) {
            return;
        }

        setRemoving(propertyId);
        const result = await unsaveProperty(propertyId);

        if (result.success) {
            NotificationManager.success('Property removed from favorites', 'Success');
        } else {
            NotificationManager.error(result.error || 'Failed to remove property', 'Error');
        }
        setRemoving(null);
    };

    const handleViewProperty = (propertyId) => {
        navigate(`/property/${propertyId}`);
    };

    // Filter properties based on search query
    const filteredProperties = properties.filter(property =>
        property.propertyName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        property.address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        property.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        property.neighborhood?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-karibu-100">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-highland-500 mx-auto mb-4"></div>
                    <p className="text-soil-600">Loading saved properties...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-karibu-100">
                <div className="text-center bg-white p-8 rounded-lg shadow-md max-w-md">
                    <AlertCircle className="h-12 w-12 text-murram-500 mx-auto mb-4" />
                    <h2 className="font-display text-xl font-semibold text-soil-900 mb-2">Error Loading Properties</h2>
                    <p className="text-soil-600 mb-4">{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-4 py-2 bg-highland-500 text-white rounded-lg hover:bg-highland-600"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-karibu-100 pt-20 pb-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="font-display text-3xl font-bold text-soil-900">Saved Properties</h1>
                    <p className="mt-2 text-soil-600">
                        Manage your favorite properties ({properties.length} saved)
                    </p>
                </div>

                {/* Search Bar */}
                {properties.length > 0 && (
                    <div className="bg-white rounded-lg shadow-sm p-4 mb-6 border border-soil-100">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-soil-400" />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search saved properties..."
                                className="w-full pl-10 pr-4 py-2 border border-soil-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-highland-500"
                            />
                        </div>
                    </div>
                )}

                {/* Properties Grid */}
                {filteredProperties.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredProperties.map((property) => (
                            <div
                                key={property.savedId}
                                className="bg-white rounded-lg shadow-sm border border-soil-100 overflow-hidden hover:shadow-md transition-shadow"
                            >
                                {/* Property Image */}
                                <div
                                    onClick={() => handleViewProperty(property.propertyId)}
                                    className="relative h-48 bg-soil-100 cursor-pointer"
                                >
                                    {property.image ? (
                                        <img
                                            src={property.image}
                                            alt={property.propertyName}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-soil-400">
                                            <Square className="h-16 w-16" />
                                        </div>
                                    )}

                                    {/* Saved Badge */}
                                    <div className="absolute top-2 right-2">
                                        <div className="bg-murram-500 text-white p-2 rounded-full">
                                            <Heart className="h-4 w-4" fill="currentColor" />
                                        </div>
                                    </div>
                                </div>

                                {/* Property Info */}
                                <div className="p-4">
                                    <h3 className="text-lg font-semibold text-soil-900 mb-2 line-clamp-1">
                                        {property.propertyName}
                                    </h3>

                                    <div className="flex items-center text-sm text-soil-600 mb-3">
                                        <MapPin className="h-4 w-4 mr-1 flex-shrink-0" />
                                        <span className="line-clamp-1">{property.address}</span>
                                    </div>

                                    {/* Price */}
                                    <div className="mb-3">
                                        <p className="text-2xl font-bold text-soil-900">
                                            {formatCurrency(property.rent, 'KES')}
                                            <span className="text-sm font-normal text-soil-600">/month</span>
                                        </p>
                                    </div>

                                    {/* Property Details */}
                                    <div className="flex items-center space-x-4 text-sm text-soil-600 mb-4 pb-4 border-b border-soil-100">
                                        {property.bedrooms !== null && (
                                            <div className="flex items-center">
                                                <Bed className="h-4 w-4 mr-1" />
                                                {property.bedrooms}
                                            </div>
                                        )}
                                        {property.bathrooms !== null && (
                                            <div className="flex items-center">
                                                <Bath className="h-4 w-4 mr-1" />
                                                {property.bathrooms}
                                            </div>
                                        )}
                                    </div>

                                    {/* Notes */}
                                    {property.notes && (
                                        <div className="mb-4">
                                            <p className="text-xs text-soil-400 mb-1">Your notes:</p>
                                            <p className="text-sm text-soil-600 line-clamp-2">{property.notes}</p>
                                        </div>
                                    )}

                                    {/* Saved Date */}
                                    <p className="text-xs text-soil-400 mb-4">
                                        Saved on {new Date(property.savedDate).toLocaleDateString('en-US', {
                                            month: 'short',
                                            day: 'numeric',
                                            year: 'numeric'
                                        })}
                                    </p>

                                    {/* Actions */}
                                    <div className="flex items-center space-x-2">
                                        <button
                                            onClick={() => handleViewProperty(property.propertyId)}
                                            className="flex-1 px-4 py-2 bg-highland-500 text-white text-sm font-medium rounded-lg hover:bg-highland-600 transition-colors"
                                        >
                                            View Details
                                        </button>
                                        <button
                                            onClick={() => handleRemove(property.propertyId)}
                                            disabled={removing === property.propertyId}
                                            className="px-4 py-2 bg-murram-500 text-white rounded-lg hover:bg-murram-600 disabled:opacity-50 transition-colors"
                                            title="Remove from favorites"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-soil-100">
                        <Heart className="mx-auto h-12 w-12 text-soil-400 mb-4" />
                        <h3 className="text-lg font-medium text-soil-900 mb-2">
                            {searchQuery ? 'No properties found' : 'No saved properties yet'}
                        </h3>
                        <p className="text-soil-600 mb-6">
                            {searchQuery
                                ? 'Try adjusting your search'
                                : 'Start browsing properties and save your favorites'}
                        </p>
                        {!searchQuery && (
                            <button
                                onClick={() => navigate('/listings')}
                                className="inline-flex items-center px-6 py-3 bg-highland-500 text-white rounded-lg font-medium hover:bg-highland-600 transition-colors"
                            >
                                Browse Properties
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SavedProperties;
